<template>
    <div class="position-relative">
        <div class="row">
            <div class="col-6">
                <div class="input-group cursor-default">
                    <input class="form-control"
                           :class="{'is-invalid': errorMessage !== null}"
                           ref="startDateInput"
                           v-model="filledStartDateValue"
                           @change="onStartDateChange">
                    <span class="input-group-text">
                        <i class="fas fa-calendar-alt"></i>
                    </span>
                </div>
            </div>
            <div class="col-6">
                <div class="input-group cursor-default">
                    <input class="form-control"
                           :class="{'is-invalid': errorMessage !== null}"
                           ref="endDateInput"
                           v-model="filledEndDateValue"
                           @change="onEndDateChange">
                    <span class="input-group-text">
                        <i class="fas fa-calendar-alt"></i>
                    </span>
                </div>
            </div>
        </div>
        <div v-if="errorMessage !== null" class="date-range-error alert alert-danger py-1 px-2 mb-0 mt-1 small d-flex align-items-center">
            <i class="fas fa-exclamation-triangle me-2"></i>{{ errorMessage }}
        </div>
    </div>
</template>

<script lang="ts">
import {DATE_FORMAT, i18n} from '@/components/DatePicker';
import moment from 'moment';
import Pikaday from 'pikaday';
import {defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

export interface DateRange {
    start: Date;
    end: Date;
}

export default defineComponent({
    props: {
        defaultDateRange: {
            type: Object as PropType<DateRange | null>,
            default: null,
        },
    },
    emits: ['change'],
    setup(props, {emit}) {
        const startDateInput = ref<HTMLElement | null>(null);
        const endDateInput = ref<HTMLElement | null>(null);
        const filledStartDateValue = ref('');
        const filledEndDateValue = ref('');
        const errorMessage = ref<string | null>(null);

        let pikadayStartDate: Pikaday;
        let pikadayEndDate: Pikaday;
        let preventSubmit = true;

        // created logic
        if (props.defaultDateRange !== null) {
            filledStartDateValue.value = moment(props.defaultDateRange.start).format(DATE_FORMAT);
            filledEndDateValue.value = moment(props.defaultDateRange.end).format(DATE_FORMAT);
        }

        function emitDateRangeChanged(): void {
            if (preventSubmit) {
                return;
            }

            if (filledStartDateValue.value === '' || filledEndDateValue.value === '') {
                errorMessage.value = null;
                return;
            }

            const start = moment(filledStartDateValue.value, DATE_FORMAT);
            const end = moment(filledEndDateValue.value, DATE_FORMAT);

            if (!start.isValid() || !end.isValid()) {
                errorMessage.value = 'Zadejte platné datum';
                return;
            }

            if (end.isBefore(start)) {
                errorMessage.value = 'Konec období nemůže být dříve než jeho začátek';
                return;
            }

            errorMessage.value = null;
            emit('change', {
                start: start.toDate(),
                end: end.toDate(),
            });
        }

        function onStartDateChange(event: any): void {
            filledStartDateValue.value = event.target.value;
            emitDateRangeChanged();
        }

        function onEndDateChange(event: any): void {
            filledEndDateValue.value = event.target.value;
            emitDateRangeChanged();
        }

        onMounted(() => {
            pikadayStartDate = new Pikaday({
                format: DATE_FORMAT,
                i18n,
                field: startDateInput.value as HTMLElement,
            });

            pikadayEndDate = new Pikaday({
                format: DATE_FORMAT,
                i18n,
                field: endDateInput.value as HTMLElement,
            });

            preventSubmit = false;
        });

        onBeforeUnmount(() => {
            pikadayStartDate.destroy();
            pikadayEndDate.destroy();
        });

        return {
            startDateInput, endDateInput,
            filledStartDateValue, filledEndDateValue, errorMessage,
            onStartDateChange, onEndDateChange,
        };
    },
});
</script>
